import { Button, Card, DatePicker, Form, Input as AntdInput, message, Modal } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import React, { useState, useCallback } from 'react';
import { QrcodeOutlined } from '@ant-design/icons';
import { isNil } from 'rambda';
import { booleanToSelectValue } from 'utils/booleanToSelectValue';
import { useSuggestions } from 'hooks/useSuggestions';
import { SuggestionField } from 'indexed_db/suggestionsDB';
import { ManufacturerSelect } from 'components/Select/Manufacturer';
import { MeterModelSelect } from 'components/Select/MeterModel';
import TextArea from 'antd/lib/input/TextArea';
import { commodityDefaultUnit, ownerTypeTranslated } from 'components/Form/meter_details/constants';
import { FormattedMessage, useIntl } from 'react-intl';
import { ThirdPartySelect } from 'components/Select/ThirdParty';
import { MeterOwnerType } from '@enmon/commons/lib/enmon';
import { Commodity } from '@enmon/commons/lib/shared';
import { useSessionStore } from '../../../../store/session';
import { QrReader } from '../../../../components/qr_reader/QrReader';
import { Select } from '../../../../components/Select/Select';
import { Input } from '../../../../components/Input/Input';
import { FormItem, FormItemProps } from '../../../../components/Form/FormItem';
import { ImageUpload } from '../../../../components/ImageUpload/ImageUpload';
import { useInventorizeStepsStore } from '../../components/store';
import { FORM_LAYOUT_CONFIG, getBuildingFloorOptions } from '../../../../consts/common';
import { selectMeterModel, useStore } from '../../useStore';
import { useScrollTopOnMount } from '../../../../hooks/useScrollTopOnMount';
import { LocationStepForm } from './slice';
import { useMeterContext } from '../../../../contexts/Meter';
import { AutoComplete } from '../../../../components/Select/AutoComplete';
import { LocationStepContent as messages } from './messages';
import { useServices } from '../../../../services/useServices';
import { useMeterModelSuggestions } from '../../../../hooks/meter_models/useMeterModelDetails';
import { useMeterModels } from '../../../../hooks/meter_models/useMeterModels';
import { useMeterModelSuggestionsStore } from '../../../../store/useMeterModelSuggestionsStore';
import { CommoditySelect } from '../../../../components/Select/Commodity';

export const LocationStepContent: React.VFC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const intl = useIntl();
  const { locale } = useSessionStore();

  const { inventoryMeter } = useMeterContext();
  const meterId = inventoryMeter?.id;

  const [form] = Form.useForm();

  const { setNextStep } = useInventorizeStepsStore();
  const {
    buildingFloor,
    calibrated,
    apiKey,
    locationBuilding,
    locationBuildings,
    manufactured,
    manufacturer,
    meterModel,
    meterOwner,
    meterOwnerType,
    meterTenant,
    commodity,
    monitoredEntity,
    newMeter,
    photoContextList,
    registers,
    internalNote,
    internalSupplyPointCode,
    resetDetailStepSlice,
    resetFloorplanStepSlice,
    resetReadingStepSlice,
    resetSubmeter,
    setDetailStepValues,
    setLocationStepValues,
    setManufacturer,
    setMeterModel,
    setPhotoContextList,
    setReadingStepValues,
    submeter,
  } = useStore();

  const { setMeterModelSuggestions, reset: resetMeterModelSuggestions } = useMeterModelSuggestionsStore();

  const { meterModels } = useMeterModels(commodity);
  const { meterModel: selectedMeterModel } = useStore(selectMeterModel(meterModels));
  const meterModelSuggestions = useMeterModelSuggestions(selectedMeterModel);

  const {
    api: { inventoryMetersApiClient },
  } = useServices();

  const locationBuildingValue = Form.useWatch<LocationStepForm['locationBuilding']>('locationBuilding', form);

  useScrollTopOnMount();

  const handleSubmit = (values: LocationStepForm) => {
    setMeterModelSuggestions(meterModelSuggestions);
    setLocationStepValues(values);
    setNextStep({ commodity, newMeter });
  };

  const handleUploadImg = (fileList: UploadFile[]) => {
    setPhotoContextList(fileList);
    form.setFieldsValue({ meterPhotoContextList: fileList });
  };

  const handleValuesChange = (changedVals: Partial<LocationStepForm>) => {
    if (typeof changedVals.newMeter !== 'undefined') {
      setDetailStepValues({
        serialNumber: undefined,
      });

      setReadingStepValues({
        meterMountType: undefined,
        readingDate: undefined,
        remoteReadingOwner: undefined,
        remoteReadingSerial: undefined,
      });
    }
    if (typeof changedVals.commodity !== 'undefined') {
      resetDetailStepSlice();
      const defaultTypeUnit = commodityDefaultUnit[changedVals.commodity];
      resetReadingStepSlice({
        registers: registers?.map(register => ({ ...register, unit: defaultTypeUnit })),
      });
      if (changedVals.commodity !== Commodity.Other) {
        resetSubmeter();
        form.setFieldsValue({ submeter: undefined });
      }
      form.setFieldsValue({ meterOwner: undefined });
    }
    if (changedVals.locationBuilding === 'empty') {
      form.setFieldsValue({ buildingFloor: 0 });
    }
    if (!isNil(changedVals.buildingFloor) && changedVals.buildingFloor !== buildingFloor) resetFloorplanStepSlice();

    setLocationStepValues(changedVals);

    if (changedVals.manufacturer || changedVals.meterModel) {
      resetMeterModelSuggestions();
    }
  };

  const handleScan = (result?: string | null) => {
    if (result) {
      form.setFieldsValue({ apiKey: result });
      setIsModalVisible(false);
      message.success(intl.formatMessage(messages.scanSuccess));
    }
  };

  const handleFormItemReset = (namePath: FormItemProps['name']) => {
    const name = Array.isArray(namePath) ? namePath.at(-1) : namePath;

    if (isNil(namePath) || isNil(name)) return;

    form.setFieldValue(namePath, null);
    setLocationStepValues({ [name]: null });
  };

  const fetchTenants = useCallback(
    () => inventoryMetersApiClient.distinct({ field: 'tenant' }),
    [inventoryMetersApiClient],
  );

  const fetchOwners = useCallback(
    () => inventoryMetersApiClient.distinct({ field: 'owner' }),
    [inventoryMetersApiClient],
  );

  const { suggestions: tenantSuggestions } = useSuggestions(SuggestionField.Tenant, fetchTenants);
  const { suggestions: ownerSuggestions } = useSuggestions(SuggestionField.Owner, fetchOwners);

  return (
    <Card bodyStyle={{ padding: 0 }} bordered={false}>
      <Form
        form={form}
        id="locationStepForm"
        layout="horizontal"
        name="locationStepForm"
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
        {...FORM_LAYOUT_CONFIG}
      >
        <FormItem
          help={<FormattedMessage {...messages.meterPhotoContextHelp} />}
          initialValue={photoContextList}
          label={<FormattedMessage {...messages.meterPhotoContext} />}
          name="meterPhotoContextList"
          required={!meterId}
          rules={[
            {
              required: !meterId,
              message: intl.formatMessage(messages.meterPhotoContextRequired),
            },
          ]}
        >
          <ImageUpload
            multiple
            fileList={photoContextList}
            name="meterPhotoContextList"
            onImageUpload={handleUploadImg}
          />
        </FormItem>

        <FormItem
          required
          initialValue={booleanToSelectValue(newMeter)}
          label={<FormattedMessage {...messages.newMeter} />}
          name="newMeter"
          rules={[
            {
              required: true,
              message: intl.formatMessage(messages.newMeterRequired),
            },
          ]}
          tooltip={<FormattedMessage {...messages.newMeterTooltip} />}
        >
          <Select
            options={[
              { label: intl.formatMessage(messages.existingMeter), value: 0 },
              { label: intl.formatMessage(messages.newMeterDraft), value: 1 },
            ]}
            placeholder={<FormattedMessage {...messages.selectValid} />}
            onChange={() => {
              form.setFieldsValue({ manufacturer: null, meterModel: null });
              setMeterModel(undefined);
              setManufacturer(undefined);
            }}
          />
        </FormItem>

        <FormItem
          required
          initialValue={commodity}
          label={<FormattedMessage {...messages.commodity} />}
          name="commodity"
          rules={[
            {
              required: true,
              message: intl.formatMessage(messages.commodityRequired),
            },
          ]}
        >
          <CommoditySelect
            className="w-100"
            disabled={!!meterId}
            onChange={() => {
              form.setFieldsValue({ manufacturer: null, meterModel: null });
              setMeterModel(undefined);
              setManufacturer(undefined);
            }}
          />
        </FormItem>

        {commodity && commodity !== Commodity.Other && (
          <FormItem
            required
            initialValue={booleanToSelectValue(submeter)}
            label={<FormattedMessage {...messages.submeter} />}
            name="submeter"
            rules={[
              {
                required: true,
                message: intl.formatMessage(messages.submeterRequired),
              },
            ]}
            tooltip={<FormattedMessage {...messages.submeterTooltip} />}
          >
            <Select
              options={[
                { label: intl.formatMessage(messages.main), value: 0 },
                { label: intl.formatMessage(messages.secondary), value: 1 },
              ]}
              placeholder={<FormattedMessage {...messages.selectValid} />}
            />
          </FormItem>
        )}

        <FormItem
          required
          initialValue={monitoredEntity}
          label={<FormattedMessage {...messages.monitoredEntity} />}
          name="monitoredEntity"
          rules={[
            {
              required: true,
              message: intl.formatMessage(messages.monitoredEntityRequired),
            },
          ]}
        >
          <Input placeholder={intl.formatMessage(messages.monitoredEntityPlaceholder)} />
        </FormItem>
        <FormItem
          required
          initialValue={locationBuilding}
          label={<FormattedMessage {...messages.building} />}
          name="locationBuilding"
          rules={[
            {
              required: true,
              message: intl.formatMessage(messages.buildingRequired),
            },
          ]}
        >
          <Select
            className="w-100"
            options={
              locationBuildings
                ? [
                    { label: intl.formatMessage(messages.outsideNotSpecified), value: 'empty' },
                    ...(locationBuildings ?? []).map(building => ({
                      label: `${building.code ? `[${building.code}]` : ''} ${building.name ? building.name : ''}`,
                      value: building.id,
                    })),
                  ]
                : []
            }
            placeholder={intl.formatMessage(messages.buildingPlaceholder)}
          />
        </FormItem>

        <FormItem initialValue={buildingFloor} label={<FormattedMessage {...messages.floor} />} name="buildingFloor">
          <Select
            allowClear
            className="w-100"
            disabled={locationBuildingValue === 'empty'}
            options={getBuildingFloorOptions(locale, intl)}
            placeholder={intl.formatMessage(messages.floorPlaceholder)}
          />
        </FormItem>

        <FormItem label={<FormattedMessage {...messages.QR} />}>
          <AntdInput.Group style={{ display: 'flex' }}>
            <FormItem noStyle initialValue={apiKey} name="apiKey">
              <Input style={{ flexGrow: 1, marginRight: 14 }} />
            </FormItem>
            <Button icon={<QrcodeOutlined />} type="primary" onClick={() => setIsModalVisible(true)}>
              <FormattedMessage {...messages.scan} />
            </Button>
          </AntdInput.Group>
        </FormItem>

        <Modal
          destroyOnClose
          footer={null}
          open={isModalVisible}
          title="Scanner"
          onCancel={() => setIsModalVisible(false)}
        >
          <h3>
            <FormattedMessage {...messages.scanQR} />
          </h3>
          <QrReader onScan={handleScan} />
        </Modal>

        {!newMeter && (
          <>
            <FormItem
              initialValue={manufacturer}
              label={<FormattedMessage {...messages.manufacturer} />}
              name="manufacturer"
            >
              <ManufacturerSelect
                commodity={commodity}
                disabled={!commodity}
                onChange={() => {
                  form.setFieldsValue({ meterModel: null });
                  setMeterModel(undefined);
                }}
              />
            </FormItem>

            <FormItem
              initialValue={meterModel}
              label={<FormattedMessage {...messages.meterModelLabel} />}
              name="meterModel"
            >
              <MeterModelSelect
                commodity={commodity}
                manufacturer={manufacturer}
                onChange={value => {
                  setMeterModel(value ?? undefined);
                  form.setFieldsValue({ meterModel: value });
                }}
                onModelManufacturerChange={nextManufacturer => {
                  form.setFieldsValue({ manufacturer: nextManufacturer });
                  setLocationStepValues({ manufacturer: nextManufacturer });
                }}
              />
            </FormItem>

            <FormItem
              initialValue={manufactured}
              isSemiRequired={!inventoryMeter?.id}
              label={<FormattedMessage {...messages.manufacturedLabel} />}
              name="manufactured"
              onResetField={handleFormItemReset}
            >
              <DatePicker inputReadOnly className="w-100" picker="year" placeholder="2018" />
            </FormItem>

            <FormItem
              initialValue={calibrated}
              isSemiRequired={!inventoryMeter?.id}
              label={<FormattedMessage {...messages.calibrated} />}
              name="calibrated"
              onResetField={handleFormItemReset}
            >
              <DatePicker inputReadOnly className="w-100" picker="year" placeholder="2020" />
            </FormItem>
          </>
        )}

        <FormItem
          required
          initialValue={meterOwnerType}
          label={<FormattedMessage {...messages.meterOwnerType} />}
          name="meterOwnerType"
          rules={[
            {
              required: true,
              message: intl.formatMessage(messages.meterOwnerTypeRequired),
            },
          ]}
        >
          <Select
            className="w-100"
            options={Object.values(MeterOwnerType).map(value => ({
              label: intl.formatMessage(ownerTypeTranslated[value]),
              value,
            }))}
            placeholder={<FormattedMessage {...messages.meterOwnerTypePlaceholder} />}
          />
        </FormItem>

        <FormItem initialValue={meterOwner} label={<FormattedMessage {...messages.meterOwner} />} name="meterOwner">
          {meterOwnerType === MeterOwnerType.UtilityCompany ? (
            <ThirdPartySelect commodity={commodity} />
          ) : (
            <AutoComplete
              placeholder={<FormattedMessage {...messages.meterOwnerPlaceholder} />}
              suggestions={ownerSuggestions ?? []}
            />
          )}
        </FormItem>

        <FormItem
          initialValue={internalSupplyPointCode}
          label={<FormattedMessage {...messages.internalSupplyPointCode} />}
          name="internalSupplyPointCode"
        >
          <Input placeholder={intl.formatMessage(messages.internalSupplyPointCodePlaceholder)} />
        </FormItem>

        <FormItem initialValue={meterTenant} label={<FormattedMessage {...messages.tenant} />} name="meterTenant">
          <AutoComplete placeholder="DB Schenker" suggestions={tenantSuggestions ?? []} />
        </FormItem>

        <FormItem initialValue={internalNote} label={<FormattedMessage {...messages.note} />} name="internalNote">
          <TextArea autoSize={{ minRows: 3, maxRows: 5 }} placeholder={intl.formatMessage(messages.notePlaceholder)} />
        </FormItem>
      </Form>
    </Card>
  );
};
